import React, { ReactElement, useEffect, useState } from 'react';
import { Modal, Form, Input, Space, Select, Checkbox, Divider, InputNumber, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  createHwcWarehouseFeeHandler,
  selectCreateHwcWareHouseFeeModal,
  selectHwcWarehouseFeeModalLoading,
  setCreateHwcWarehouseFeeModal
} from '../../../../redux/hwcWarehouseFeeSlice';
import { HwcWarehouseFeeData } from '../../../../custom_types/redux-types';
import { WeightUnit } from '../../../../shared/utils/constants';

const { Option } = Select;

const HwcWarehouseFeeCreateModal = (): ReactElement => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const showModal = useSelector(selectCreateHwcWareHouseFeeModal);
  const modalLoading = useSelector(selectHwcWarehouseFeeModalLoading);

  useEffect(() => {
    form.resetFields();
  }, [form, dispatch]);

  const cancelClickedHandler = () => {
    form.resetFields();
    dispatch(setCreateHwcWarehouseFeeModal(false));
  };

  const okClickedHandler = () => {
    form
      .validateFields()
      .then((values) => {
        const data: HwcWarehouseFeeData = {
          weightRange: values.weightRange,
          weightUnit: values.weightUnit,
          baseFee: values.baseFee,
          countingFee: values.countingFee,
          skuFee: values.skuFee,
          packingFee: values.packingFee,
          labelFee: values.labelFee,
          returnFee: values.returnFee,
          storageFee: values.storageFee,
          otherFees: values.otherFees
        };
        console.log(data);
        dispatch(createHwcWarehouseFeeHandler(data));
        form.resetFields();
      })
      .catch(() => {});
  };

  return (
    <Modal
      width={600}
      styles={{
        body: {
          minHeight: '300px',
          maxHeight: '1000px',
          overflowY: 'auto',
          margin: '10px 50px'
        }
      }}
      centered
      closable={false}
      open={showModal}
      okText="添加仓库操作费"
      cancelText="取消"
      onCancel={cancelClickedHandler}
      onOk={okClickedHandler}
      okButtonProps={{ loading: modalLoading }}
    >
      <Form form={form} layout="vertical">
        <Space size="large">
          <Form.Item label="重量范围" name="weightRange">
            <Input placeholder="重量范围" />
          </Form.Item>
          <Form.Item label="重量单位" name="weightUnit">
            <Select placeholder="重量单位" style={{ width: 180 }}>
              {Object.values(WeightUnit).map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Space>
        <div>
          <Space size="large">
            <Form.Item
              label="基础费用"
              name="baseFee"
              rules={[{ required: true, message: '基础费用必须填' }]}
            >
              <InputNumber placeholder="基础费用" />
            </Form.Item>
            <Form.Item
              label="清点费"
              name="countingFee"
              rules={[{ required: true, message: '清点费用必须填' }]}
            >
              <InputNumber placeholder="清点费" />
            </Form.Item>
          </Space>
        </div>
        <div>
          <Space size="large">
            <Form.Item
              label="多SKU费用"
              name="skuFee"
              rules={[{ required: true, message: '多SKU费用必须填' }]}
            >
              <InputNumber placeholder="多SKU费用" />
            </Form.Item>
            <Form.Item
              label="包装费"
              name="packingFee"
              rules={[{ required: true, message: '包装费必须填' }]}
            >
              <InputNumber placeholder="包装费" />
            </Form.Item>
          </Space>
        </div>
        <div>
          <Space size="large">
            <Form.Item
              label="自提面单费"
              name="labelFee"
              rules={[{ required: true, message: '自提面单费必须填' }]}
            >
              <InputNumber placeholder="自提面单费" />
            </Form.Item>
            <Form.Item
              label="退货费"
              name="returnFee"
              rules={[{ required: true, message: '退货费必须填' }]}
            >
              <InputNumber placeholder="退货费" />
            </Form.Item>
          </Space>
        </div>
        <Form.List name="storageFee">
          {(fields, { add, remove }) => (
            <>
              <Divider>仓储费</Divider>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} size="large">
                  <Form.Item {...restField} label="体积" name={[name, 'volumnRange']}>
                    <Input placeholder="体积范围" />
                  </Form.Item>
                  <Form.Item {...restField} label="费用" name={[name, 'fee']}>
                    <InputNumber placeholder="费用" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  style={{ width: '60%' }}
                  icon={<PlusOutlined />}
                >
                  添加仓储费
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
        <Form.List name="otherFees">
          {(fields, { add, remove }) => (
            <>
              <Divider>其他收费</Divider>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} size="large">
                  <Form.Item {...restField} label="费种" name={[name, 'type']} initialValue="sku">
                    <Select placeholder="费种" options={[{ label: 'SKU', value: 'sku' }]} />
                  </Form.Item>
                  <Form.Item {...restField} label="值" name={[name, 'value']}>
                    <Input placeholder="值" />
                  </Form.Item>
                  <Form.Item {...restField} label="费用" name={[name, 'fee']}>
                    <InputNumber placeholder="费用" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  style={{ width: '60%' }}
                  icon={<PlusOutlined />}
                >
                  添加费用
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default HwcWarehouseFeeCreateModal;
